import { useEffect, useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { getAboutData } from "@/api/aboutApi";
import { submitLead } from "@/api/leadApi";
import { assetUrl } from "@/utils/assetUrl";
import { useSEO, buildBreadcrumbLd } from "@/utils/seo";
import { buildContactPageLd, buildOrganizationLd } from "@/seo/jsonld";
import { readAboutPreload } from "@/utils/aboutPreload";
import FaqBlock from "@/components/feature/FaqBlock";

interface ContactInfo {
  companyName: string;
  phone: string;
  email: string;
  address: string;
}

const EMPTY_CONTACT: ContactInfo = { companyName: "", phone: "", email: "", address: "" };

function ContactLeadForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setLoading(true);
    const formData = new FormData(form);
    try {
      await submitLead({
        company: formData.get("company") as string,
        name: formData.get("name") as string,
        phone: formData.get("phone") as string,
        email: (formData.get("email") as string) || "",
        service: formData.get("service") as string,
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-12 border border-gray-100 text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-5">
          <i className="ri-check-line text-3xl text-green-500"></i>
        </div>
        <h3 className="text-xl font-bold text-[#0B1B3D] mb-2">提交成功！</h3>
        <p className="text-gray-500 text-sm">感谢您的留言，我们的专属客服将在1个工作日内与您联系。</p>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl p-8 lg:p-10 border border-gray-100"
    >
      <h3 className="text-lg font-bold text-[#0B1B3D] mb-6">在线留言</h3>

      <div className="mb-5">
        <label className="block text-sm font-medium text-[#333333] mb-1.5">
          企业名称 <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="company"
          required
          placeholder="请输入贵公司名称"
          className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a56db] focus:ring-1 focus:ring-[#1a56db]/20 transition-colors bg-white text-[#333333] placeholder-gray-400"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <div>
          <label className="block text-sm font-medium text-[#333333] mb-1.5">
            联系人 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            required
            placeholder="请输入联系人姓名"
            className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a56db] focus:ring-1 focus:ring-[#1a56db]/20 transition-colors bg-white text-[#333333] placeholder-gray-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#333333] mb-1.5">
            手机号码 <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            required
            placeholder="请输入手机号码"
            className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a56db] focus:ring-1 focus:ring-[#1a56db]/20 transition-colors bg-white text-[#333333] placeholder-gray-400"
          />
        </div>
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-[#333333] mb-1.5">邮箱地址</label>
        <input
          type="email"
          name="email"
          placeholder="请输入邮箱（选填）"
          className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a56db] focus:ring-1 focus:ring-[#1a56db]/20 transition-colors bg-white text-[#333333] placeholder-gray-400"
        />
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-[#333333] mb-1.5">
          感兴趣的服务 <span className="text-red-500">*</span>
        </label>
        <select
          name="service"
          required
          className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1a56db] focus:ring-1 focus:ring-[#1a56db]/20 transition-colors bg-white text-[#333333] cursor-pointer"
        >
          <option value="">请选择感兴趣的服务方向</option>
          <option value="网络安全综合防护">网络安全综合防护</option>
          <option value="终端安全与数据保护">终端安全与数据保护</option>
          <option value="安全运营与应急响应">安全运营与应急响应</option>
          <option value="IT基础架构规划建设">IT基础架构规划建设</option>
          <option value="云计算迁移与建设">云计算迁移与建设</option>
          <option value="云信达解决方案">云信达解决方案</option>
          <option value="AI业务解决方案">AI业务解决方案</option>
          <option value="等保合规咨询">等保合规咨询</option>
          <option value="综合安全解决方案">综合安全解决方案</option>
        </select>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-4 bg-[#1a56db] hover:bg-[#1e40af] disabled:opacity-60 text-white font-bold rounded-lg transition-all duration-200 text-sm whitespace-nowrap cursor-pointer tracking-wide"
      >
        {loading ? "提交中..." : "提交留言"}
      </button>

      <p className="text-center text-xs text-gray-400 mt-3">
        提交即代表您同意我们的隐私政策，您的信息将被严格保密
      </p>
    </form>
  );
}

function initialContact(): ContactInfo {
  const c = readAboutPreload()?.contact;
  if (!c) return EMPTY_CONTACT;
  return {
    companyName: c.companyName || "",
    phone: c.phone || "",
    email: c.email || "",
    address: c.address || "",
  };
}

export default function ContactPage() {
  const [contact, setContact] = useState<ContactInfo>(initialContact);
  const [officeImage, setOfficeImage] = useState<string>(
    () => readAboutPreload()?.companyIntro?.image ?? "",
  );

  useEffect(() => {
    getAboutData()
      .then((res: any) => {
        const c = res?.contact || {};
        setContact({
          companyName: c.companyName || "",
          phone: c.phone || "",
          email: c.email || "",
          address: c.address || "",
        });
        if (res?.companyIntro?.image) setOfficeImage(res.companyIntro.image);
      })
      .catch(() => {});
  }, []);

  const companyDisplayName = contact.companyName || "欧格登智能科技";

  // `ContactPage` JSON-LD 的 `contactPoint` 复用 `buildOrganizationLd()` 的构造结果
  // （需求 4.10、Design「新增 /contact 页面」一节），而不是重新实现一遍电话字段的
  // 归一逻辑——传入与 `/api/front/about` 同源的 `phone` / `address` / `areaServed`
  // 缺省，`buildOrganizationLd()` 在电话字段空白时会省略 `contactPoint`，此时
  // `buildContactPageLd()` 自带的兜底元素保证「至少 1 个 ContactPoint」这条硬性约束。
  const { jsonLd: organizationLd } = buildOrganizationLd({
    companyName: contact.companyName,
    phone: contact.phone,
    address: contact.address,
  });

  useSEO({
    title: "联系我们 - 欧格登智能科技电话邮箱与办公地址",
    description:
      "联系欧格登智能科技获取信息安全、IT基础架构、云信达与AI业务解决方案咨询，提供联系电话、电子邮箱、办公地址与在线留言入口，工作日内快速响应。",
    keywords: ["联系我们", "欧格登电话", "欧格登邮箱", "欧格登地址", "在线留言", "方案咨询"],
    path: "/contact",
    pageType: "CONTACT",
    jsonLd: [
      buildBreadcrumbLd([
        { name: "首页", path: "/" },
        { name: "联系我们", path: "/contact" },
      ]),
      buildContactPageLd(
        { name: "联系我们", url: "/contact" },
        companyDisplayName,
        organizationLd.contactPoint,
      ),
    ],
  });

  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative bg-[#0d1f3c] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20corporate%20reception%20area%2C%20modern%20technology%20company%20office%20lobby%2C%20enterprise%20business%20environment%2C%20navy%20blue%20interior%2C%20clean%20professional%20atmosphere&width=1920&height=500&seq=contacthero001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f3c]/60 to-[#0d1f3c]"></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/20 border border-[#1a56db]/40 rounded-full mb-5">
            <span className="text-[#4db8ff] text-xs font-medium">联系我们</span>
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            联系{companyDisplayName}
          </h1>
          <p className="text-gray-400 text-sm lg:text-base max-w-2xl mx-auto">
            无论您正在评估信息安全建设、规划 IT 基础架构升级，还是希望了解云信达与AI业务的落地方式，我们的专属客服团队都会在 1 个工作日内与您取得联系。
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <p className="text-sm lg:text-base text-gray-500 leading-relaxed">
              {companyDisplayName}深耕信息安全与企业级 IT 服务十余年，已为数百家政企客户提供从需求诊断、方案设计到实施运维的一体化服务。
              如果您希望进一步了解我们的能力，可以先浏览
              <Link to="/about" className="text-[#1a56db] hover:underline mx-1">
                关于我们
              </Link>
              页面，了解公司发展历程与资质荣誉；也可以查看
              <Link to="/solutions" className="text-[#1a56db] hover:underline mx-1">
                解决方案
              </Link>
              页面，了解信息安全、IT基础架构、云信达与AI业务四大领域的典型架构与落地案例。无论您处于需求梳理的早期阶段，还是已有明确的项目预算与时间表，
              我们都建议先通过下方的联系方式或在线留言与我们取得联系，专属顾问会结合您的行业特点与现状给出更具体的建议。
            </p>
          </div>

          {/* Contact Info */}
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0d1f3c] text-center mb-10">联系方式</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
            <div className="bg-[#F4F7FC] border border-gray-200 rounded-xl p-6 text-center hover:border-[#1a56db]/25 transition-all duration-200">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1a56db]/8 mx-auto mb-3">
                <i className="ri-building-2-line text-xl text-[#1a56db]"></i>
              </div>
              <div className="text-xs text-gray-400 mb-1">企业名称</div>
              <div className="text-sm font-semibold text-[#0d1f3c]">{companyDisplayName}</div>
            </div>
            <div className="bg-[#F4F7FC] border border-gray-200 rounded-xl p-6 text-center hover:border-[#1a56db]/25 transition-all duration-200">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1a56db]/8 mx-auto mb-3">
                <i className="ri-phone-line text-xl text-[#1a56db]"></i>
              </div>
              <div className="text-xs text-gray-400 mb-1">服务热线</div>
              {contact.phone ? (
                <a href={`tel:${contact.phone}`} className="text-sm font-semibold text-[#0d1f3c] hover:text-[#1a56db]">
                  {contact.phone}
                </a>
              ) : (
                <div className="text-sm font-semibold text-[#0d1f3c]">暂未提供</div>
              )}
              <div className="text-xs text-gray-400 mt-1">工作日 9:00-18:00</div>
            </div>
            <div className="bg-[#F4F7FC] border border-gray-200 rounded-xl p-6 text-center hover:border-[#1a56db]/25 transition-all duration-200">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1a56db]/8 mx-auto mb-3">
                <i className="ri-mail-line text-xl text-[#1a56db]"></i>
              </div>
              <div className="text-xs text-gray-400 mb-1">邮箱</div>
              {contact.email ? (
                <a href={`mailto:${contact.email}`} className="text-sm font-semibold text-[#0d1f3c] hover:text-[#1a56db]">
                  {contact.email}
                </a>
              ) : (
                <div className="text-sm font-semibold text-[#0d1f3c]">暂未提供</div>
              )}
              <div className="text-xs text-gray-400 mt-1">1个工作日内回复</div>
            </div>
            <div className="bg-[#F4F7FC] border border-gray-200 rounded-xl p-6 text-center hover:border-[#1a56db]/25 transition-all duration-200">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1a56db]/8 mx-auto mb-3">
                <i className="ri-map-pin-line text-xl text-[#1a56db]"></i>
              </div>
              <div className="text-xs text-gray-400 mb-1">办公地址</div>
              <div className="text-sm font-semibold text-[#0d1f3c]">{contact.address || "暂未提供"}</div>
              <div className="text-xs text-gray-400 mt-1">欢迎预约参观</div>
            </div>
          </div>

          {/* Office image */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="w-full h-64 lg:h-80 rounded-2xl overflow-hidden">
              <img
                src={
                  officeImage
                    ? assetUrl(officeImage)
                    : "https://readdy.ai/api/search-image?query=modern%20technology%20company%20office%20interior%2C%20professional%20workspace%2C%20enterprise%20business%20environment%2C%20clean%20bright%20atmosphere&width=1200&height=600&seq=contactoffice001&orientation=landscape"
                }
                alt={`${companyDisplayName}办公环境实景`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Lead form */}
          <div className="max-w-2xl mx-auto" id="contact-lead-form">
            <div className="text-center mb-8">
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0d1f3c] mb-3">在线留言咨询</h2>
              <p className="text-sm text-gray-500">
                填写下方表单，留下您的联系方式与感兴趣的服务方向，我们的专属顾问将尽快与您沟通具体需求。
              </p>
            </div>
            <ContactLeadForm />
          </div>
        </div>
      </section>

      <FaqBlock scope="CONTACT" />
    </main>
  );
}
